import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, Subscription, take } from 'rxjs';
import { PageUtils } from '../common/page.utils';
import { ErrorMessages } from '../constants/error-messages';
import { Messages } from '../constants/messages';
import { MetadataEntryType } from '../constants/metadata-entry-type';
import {
  AssessmentItemPosition,
  AssessmentItemTag,
  AssessmentUserResponseStatus,
  LearnosityItemType,
  ScoreAndTime,
} from '../models/assessment-models';
import { Item } from '../models/learnosity-item-models/item';
import { Question } from '../models/learnosity-item-models/question';
import { URLs } from '../models/urls';
import { updateAssessment } from '../store/actions/assessment.actions';
import { nextStep, noNav, repeatInstructions } from '../store/actions/page-content.actions';
import { pageLoadingFinish, pageLoadingStart } from '../store/actions/page-loading.actions';
import { loadNextPage } from '../store/actions/page.actions';
import { PageContentState, PageState } from '../store/models/assessment-models';
import {
  selectUpdateAssessment,
  selectUpdateAssessmentError,
  selectUpdateAssessmentLoading,
} from '../store/selectors/assessment.selectors';
import { LazyLoadScriptService } from './lazy-load-script.service';
import { TimeTrackingService } from './time-tracking.service';
import { LoggingService } from './logging.service';

/**
 * @description Learnosity Instance Object
 */
declare const LearnosityItems: any;

// Private
@Injectable({
  providedIn: 'root',
})
export class AssessmentPlayerService {
  /**
   * @description Instance of Aptem Assessment Testlet loaded in DOM.
   */
  private itemsApp: any;
  private currentTestlet: string;
  private sessionId: string;
  private assessmentId: number;

  public questionLoadEventNotifier$ = new Subject<AssessmentItemPosition>();
  public questionChangeEventNotifier$ = new Subject<AssessmentUserResponseStatus>();
  public sectionChangeEventNotifier$ = new Subject<void>();
  public testSubmitBeginNotifier$ = new Subject<void>();
  public testUnloadEventNotifier$ = new Subject<void>();
  public initObjectChangeSubscription: Subscription;

  private instructionFirstPageIndex = 0;

  private submitHandler = {
    error: () => this.store.dispatch(pageLoadingFinish()),
    progress: () => this.store.dispatch(pageLoadingStart({ description: Messages.responseSavingMessage })),
    show_submit_confirmation: false,
    show_submit_ui: false,
  };

  constructor(
    private readonly scriptLoader: LazyLoadScriptService,
    private readonly timeTracking: TimeTrackingService,
    private readonly router: Router,
    private readonly store: Store<PageState>,
    private readonly pageContentStore: Store<PageContentState>,
    private readonly pageUtils: PageUtils,
    private readonly route: ActivatedRoute,
    private readonly loggingService: LoggingService,
  ) {}

  private assessmentUpdateState = this.store.select(selectUpdateAssessment);
  private assessmentUpdateIsLoading = this.store.select(selectUpdateAssessmentLoading);
  private assessmentUpdateError = this.store.select(selectUpdateAssessmentError);

  public initializeTestlet(initializationObject: any, currentTestlet: string, sessionId: string, assessmentId: number) {
    this.scriptLoader
      .loadScript(URLs.learnosityAPI)
      .pipe(take(1))
      .subscribe(() => {
        this.destroyItemsApp();

        this.pageContentStore.dispatch(pageLoadingStart({ description: Messages.loadingMessage }));
        this.itemsApp = LearnosityItems.init(initializationObject, this.itemsAppListeners);
        this.currentTestlet = currentTestlet;
        this.sessionId = sessionId;
        this.assessmentId = assessmentId;
      });
  }

  private get itemsAppListeners() {
    return {
      readyListener: () => {
        this.store.dispatch(pageLoadingFinish());
        this.setEventListeners();
      },
      errorListener: () => {
        this.store.dispatch(pageLoadingFinish());
        this.pageUtils.navigateToErrorPage(ErrorMessages.testletRequestError);
      },
    };
  }

  /**
   * @description Reset already existing itemsApp instance before creating new.
   */
  public destroyItemsApp() {
    if (this.itemsApp) {
      this.itemsApp.reset();
      this.itemsApp.off();
      this.itemsApp = null;
    }
  }

  /**
   * @description Triggering event listener set up methods.
   */
  private setEventListeners() {
    // "Item" can be either Instructions/Question/Stimulus.
    this.onItemLoad();

    this.setSectionChangeListener();
    this.onQuestionUnload();
    this.onTestAutoSubmit();
    this.onTestSubmitSuccess();
  }

  /**
   * @description Event listener for Learnosity when a new 'Item' loaded.
   * Will be called on each item load and needed for setting proper navigation buttons.
   */
  private onItemLoad() {
    this.itemsApp.on('item:load', () => {
      const itemTags = this.getCurrentItemTags;

      if (!itemTags) {
        return;
      }

      if (itemTags.item === LearnosityItemType.Introduction) {
        this.pageContentStore.dispatch(nextStep());
      }

      if (itemTags.item === LearnosityItemType.Instruction) {
        this.setInstructionPageControls(this.getCurrentItemPosition(false));
      }

      if (itemTags.item === LearnosityItemType.Question) {
        this.timeTracking.trackMetadataEntry(this.currentItem.reference, MetadataEntryType.QuestionLoad);
        this.questionLoadEventNotifier$.next(this.getCurrentItemPosition(true));
        this.pageContentStore.dispatch(noNav());
      }

      if (itemTags.item === LearnosityItemType.Stimulus) {
        this.timeTracking.trackMetadataEntry(this.currentItem.reference, MetadataEntryType.StimulusLoad);
        this.pageContentStore.dispatch(noNav());
      }
    });
  }

  /**
   * @description Event listener for Learnosity when a new 'Section' is loaded.
   */
  private setSectionChangeListener() {
    this.itemsApp.on('section:changed', () => this.setResponseChangeListener());
  }

  /**
   * @description Event listener for Learnosity item unload event.
   */
  private onQuestionUnload() {
    this.itemsApp.on('item:beforeunload', (e: any) => {
      this.trackUnload();
    });

    this.itemsApp.on('item:unload', () => {});
  }

  private trackUnload() {
    this.timeTracking.trackUnloadEntry();
    const currentItem = this.itemsApp?.getCurrentItem?.() as Item | undefined;

    if (!currentItem) {
      this.loggingService.logEvent('AssessmentPlayer.TrackUnload.MissingItem', {
        currentTestlet: this.currentTestlet ?? null,
        sessionId: this.sessionId ?? null,
        assessmentId: this.assessmentId ?? null,
      });
      return;
    }

    const responseIds = currentItem.response_ids ?? [];
    if (!responseIds.length) {
      this.loggingService.logEvent('AssessmentPlayer.TrackUnload.NoResponses', {
        itemReference: currentItem.reference,
        currentTestlet: this.currentTestlet ?? null,
        sessionId: this.sessionId ?? null,
        assessmentId: this.assessmentId ?? null,
      });
      return;
    }

    const respId = responseIds[0];
    const question = this.itemsApp?.question?.(respId) as Question | undefined;

    if (!question) {
      this.loggingService.logEvent('AssessmentPlayer.TrackUnload.MissingQuestion', {
        itemReference: currentItem.reference,
        responseId: respId,
        currentTestlet: this.currentTestlet ?? null,
        sessionId: this.sessionId ?? null,
        assessmentId: this.assessmentId ?? null,
      });
      return;
    }

    const response = question.getResponse?.();

    if (!response) {
      return;
    }

    const metadata = question.response?.getMetadata?.() ?? {};
    const loadTimestamp = this.timeTracking.getMetaEntry(MetadataEntryType.QuestionLoad, currentItem.reference);
    const unloadTimestamp = this.timeTracking.getMetaEntry(MetadataEntryType.QuestionUnload, currentItem.reference);

    const updatedMetadata = {
      ...metadata,
      ...(loadTimestamp != null ? { item_load: loadTimestamp.toString() } : {}),
      ...(unloadTimestamp != null ? { item_unload: unloadTimestamp.toString() } : {}),
    };

    question.response?.setMetadata?.(updatedMetadata);
  }

  /**
   * @description Event listener for Learnosity when assessment is 'Submitted'.
   * Initialize navigation to Test/Assessment Progress.
   */
  private onTestAutoSubmit() {
    this.itemsApp.on('test:submit', () => {
      this.trackUnload();
      this.timeTracking.closeTimeTracking();
      this.store.dispatch(pageLoadingStart({ description: Messages.responseSavingMessage }));
      this.testSubmitBeginNotifier$.next();
    });
  }

  /**
   * @description Event listener for Learnosity when assessment is 'Successfully' submitted.
   */
  private onTestSubmitSuccess() {
    this.itemsApp.on('test:submit:success', () => {
      const testletScoreAndTime = this.getTestletScoreAndTime();
      const snapshot = this.route.snapshot.firstChild ?? this.route.snapshot;
      const routeParams = this.pageUtils.getRoutePathParameters(snapshot);
      this.timeTracking.logMetadataCsv();
      this.timeTracking.clearMetadataEntries();
      this.destroyItemsApp();
      this.store.dispatch(pageLoadingFinish());
      this.store.dispatch(
        updateAssessment({
          orgId: routeParams.orgCode,
          campaignId: routeParams.campaignCode,
          assessmentId: this.assessmentId,
          invitationCode: routeParams.invitationCode,
          currentTestlet: this.currentTestlet,
          sessionId: this.sessionId,
          score: testletScoreAndTime.score,
          duration: testletScoreAndTime.responseTime,
        }),
      );
      this.assessmentUpdateState.subscribe((updateAssessment) => {
        if (
          updateAssessment != null &&
          !updateAssessment.testletConfig.loading &&
          updateAssessment.testletConfig.error == null &&
          updateAssessment.testletConfig.data != null
        ) {
          // Re-take the testlet or move onto the next one
          if (
            this.pageUtils.getTestletByName(this.currentTestlet) ==
            this.pageUtils.getTestletByName(updateAssessment.testletConfig.data.nextTestlet)
          ) {
            const url = this.pageUtils.buildParameterisedRoute(
              this.pageUtils.getNextPage(updateAssessment.testletConfig.data.nextTestlet),
            );
            this.router.navigateByUrl(url);
          } else {
            const url = this.pageUtils.buildParameterisedRoute('overview');
            this.store.dispatch(
              loadNextPage({
                nextPage: updateAssessment.testletConfig.data.nextTestlet,
              }),
            );
            this.router.navigateByUrl(url);
          }
        }
      });
    });
  }

  /**
   * @description Event listener for Learnosity when a 'Question Item' changes.
   * Will be called when user interact with response options.
   * Pass new response timestamp and values to service which handles metadata.
   */
  private setResponseChangeListener() {
    const questions = this.itemsApp.questions();
    // @ts-ignore
    Object.values(questions).forEach((question: Question) => {
      question.on('changed', () => {
        const responses = question.getResponse()?.value;
        const attemptedStatus = this.currentItem?.attempt_status;
        this.questionChangeEventNotifier$.next({ responses, attemptedStatus });
      });
    });
  }

  /**
   * @description Returns loaded 'Item'.
   */
  private get currentItem() {
    return this.itemsApp?.getCurrentItem() as Item;
  }

  /**
   * @description Returns tags of loaded 'Item' if present.
   */
  private get getCurrentItemTags(): AssessmentItemTag | null {
    const itemReference = this.currentItem.reference;
    const itemTags = this.itemsApp?.getTags(true);
    const currentItemTag = itemTags[`${itemReference}`];
    if (!currentItemTag) {
      return null;
    }
    const item = currentItemTag?.ItemType[0];
    const position = currentItemTag?.Position?.[0];
    return { item, position };
  }

  /**
   * @description Returns index of the current 'Item' along with flags to indicate
   * whether it is the first or last 'Item' in the list.
   */
  private getCurrentItemPosition(isQuestionItem: boolean): AssessmentItemPosition {
    const itemList = Object.keys(isQuestionItem ? this.itemsApp?.questions() : this.itemsApp?.getItems());
    const currentItemIndex = itemList.indexOf(
      isQuestionItem ? this.getCurrentItemResponseId : this.currentItem.reference,
    );
    return {
      currentItemIndex,
      isFirstItem: currentItemIndex === 0,
      isLastItem: currentItemIndex === itemList.length - 1,
    };
  }

  /**
   * @description Fetch the current index of loaded 'Item' from the Learnosity Questions Array.
   */
  private getItemIndex(itemReference: string) {
    return this.itemsApp?.assessApp().getItemPosition(itemReference);
  }

  /**
   * @description To get reference of Learnosity items object.
   */
  private get getInstanceOfItem() {
    return this.itemsApp.items();
  }

  /**
   * @description Returns the unique responseId associated with the current question.
   */
  private get getCurrentItemResponseId() {
    const currentItem = this.currentItem;
    return currentItem.response_ids[0];
  }

  /**
   * @description Load/navigate to the next Learnosity item.
   */
  public goToNextItem() {
    const itemTags = this.getCurrentItemTags;
    const isQuestion = itemTags != null && itemTags.item === LearnosityItemType.Question;
    const position = this.getCurrentItemPosition(isQuestion);

    if (isQuestion && position.isLastItem) {
      this.finishAssessment();
    } else {
      const items = this.getInstanceOfItem;
      items.next().then();
    }
  }

  public isLastInstruction() {
    if (this.currentItem == null && this.itemsApp == null) {
      return false;
    }

    const itemTags = this.getCurrentItemTags;
    const isInstruction = itemTags != null && itemTags.item === LearnosityItemType.Instruction;
    const position = this.getCurrentItemPosition(false);

    return isInstruction && position.isLastItem;
  }
  /**
   * @description Navigate to first Instruction Page Item.
   */
  public repeatInstructions() {
    const items = this.getInstanceOfItem;
    items.goto(this.instructionFirstPageIndex).then();
  }

  /**
   * @description Learnosity method to submit the 'Activity' and save user responses to Learnosity.
   */
  public finishAssessment() {
    this.trackUnload();
    this.timeTracking.closeTimeTracking();
    this.itemsApp?.submit(this.submitHandler);
    this.testUnloadEventNotifier$.next();
  }

  /**
   * @description Calculate the score and response time for the testlet,
   * so we don't need to fetch this from Learnosity in the loading of the next.
   */
  private getTestletScoreAndTime(): ScoreAndTime {
    const scores = this.itemsApp.getItemScores();
    const score = +Object.values(scores).reduce((sum: number, current: any) => sum + +current.score, 0);
    const time = this.timeTracking.calculateAssessmentTime();

    // In case all questions were answered in time, return response time minus stimulus questions display time
    return { score, responseTime: time };
  }

  /**
   * @description Updates values of page navigation flags for instruction page.
   * @param itemPosition
   */
  public setInstructionPageControls(itemPosition: AssessmentItemPosition) {
    if (itemPosition.isLastItem) {
      this.pageContentStore.dispatch(repeatInstructions());
      return;
    }

    this.pageContentStore.dispatch(nextStep());

    if (itemPosition.isFirstItem) {
      this.instructionFirstPageIndex = this.getItemIndex(this.currentItem.reference);
    }
  }
}
